import { Badge } from '../atoms';
import type { SiteConstraint } from '../api/locate';
import { buildSiteFacts } from '../../../services/planning-assessment-service/src/siteFacts';

const km = (m: number): string => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);

function parseConstraints(siteLocation: string | null | undefined): SiteConstraint[] | null {
  if (!siteLocation) return null;
  try {
    const v = JSON.parse(siteLocation) as { constraints?: unknown };
    return Array.isArray(v.constraints) ? (v.constraints as SiteConstraint[]) : [];
  } catch {
    return null;
  }
}

function resultOf(c: SiteConstraint): { kind: string; text: string } {
  if (c.status === 'Not checked') return { kind: 'warn', text: 'Not checked' };
  if (c.status === 'Absent') return { kind: 'neutral', text: c.patchy ? 'None recorded' : 'Not present' };
  return c.edgeOnly ? { kind: 'neutral', text: 'Edge of site' } : { kind: 'pos', text: 'Present' };
}

const sharePct = (v: number): string => `${v.toFixed(1)}%`;

function detailOf(c: SiteConstraint): string {
  if (c.status === 'Not checked') return c.reason ?? 'The data source could not be queried.';
  if (c.status === 'Absent') return c.patchy ? 'Local planning authorities supply this data patchily, so this does not confirm there is none.' : '';
  if (c.edgeOnly) {
    return typeof c.coveragePct === 'number'
      ? `About ${sharePct(c.coveragePct)} of the traced outline overlaps it, within the placement error of the traced outline. Not treated as present unless the planning statement says so.`
      : 'Only a sliver along the edge of the search area, none reaching inside the traced outline.';
  }
  const parts: string[] = [];
  const names = Array.isArray(c.names) ? c.names : [];
  if (c.group === 'Transport') {
    const from = c.measuredFrom === 'boundary' ? 'site boundary' : 'pin';
    if (names[0]) parts.push(names[0]);
    if (typeof c.distanceM === 'number') parts.push(c.distanceM === 0 ? 'inside the site boundary' : `${km(c.distanceM)} from the ${from}`);
    if (typeof c.within400M === 'number' && typeof c.within800M === 'number') parts.push(`${c.within400M} within 400 m, ${c.within800M} within 800 m`);
    const bus = c.busService?.nearest;
    if (bus === 'unknown') parts.push('bus frequency unknown (not covered by published timetables)');
    else if (bus === 'none') parts.push('no scheduled departures at the nearest stop');
    else if (bus) parts.push(`${bus.weekday} weekday buses at the nearest stop (${bus.peakHour} in the busiest hour, ${bus.services} routes)`);
    return parts.join(' · ');
  }
  const shown = names.slice(0, 12);
  if (shown.length) parts.push(shown.join('; '));
  if (c.count > shown.length) parts.push(`${c.count} in total`);
  else if (!shown.length && c.count) parts.push(`${c.count} found`);
  if (typeof c.coveragePct === 'number') parts.push(`covers about ${sharePct(c.coveragePct)} of the traced outline`);
  return parts.join(' · ');
}

/** Every designation and transport result recorded for the case, including the ones that were absent or not checked. */
export function SiteFeaturesCard({ siteLocation, loading }: { siteLocation: string | null | undefined; loading: boolean }) {
  const rows = parseConstraints(siteLocation);
  return (
    <div className="card">
      <div className="card-hd">
        Site features
        <span className="card-hd-sub">Every check made against the located site, including those that found nothing.</span>
      </div>
      <div className="card-body flush">
        {loading ? <div style={{ padding: 16 }}><span className="skel" /></div> : !rows || rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12.5 }}>No site features were recorded for this case.</div>
        ) : (
          <table className="tbl tbl-list">
            <thead>
              <tr>
                <th>Feature</th>
                <th className="hide-mobile">Group</th>
                <th>Result</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => {
                const r = resultOf(c);
                return (
                  <tr key={c.dataset}>
                    <td className="primary">{c.label || c.dataset}</td>
                    <td className="hide-mobile">{c.group ?? 'Designation'}</td>
                    <td><Badge kind={r.kind}>{r.text}</Badge></td>
                    <td style={{ whiteSpace: 'normal', color: 'var(--ink-2)' }}>{detailOf(c) || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** The confirmed site facts block exactly as the assessment stage builds it from the saved location. */
export function AssessInputCard({ siteLocation, loading }: { siteLocation: string | null | undefined; loading: boolean }) {
  const facts = buildSiteFacts(siteLocation);
  return (
    <div className="card">
      <div className="card-hd">
        Derived data passed to the assessment
        <span className="card-hd-sub">This text is added to the prompt, and the model treats it as authoritative for designations.</span>
      </div>
      <div className="card-body">
        {loading ? <span className="skel" /> : facts ? (
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 12, lineHeight: 1.5, fontFamily: 'var(--mono, monospace)' }}>{facts}</pre>
        ) : (
          <span style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>No site location was established for this case, so the assessment received no site facts.</span>
        )}
      </div>
    </div>
  );
}
